"use client";

import Image from "next/image";
import Link from "next/link";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type TouchEvent,
} from "react";
import {
  getHeroSliderCtaClassName,
  getHeroSliderNavButtonClassName,
  heroBodyFont,
  heroDisplayFont,
  resolveHeroSliderTone,
} from "@/components/hero-slider-typography";
import { VideoPlayer } from "@/components/video-player";
import type {
  BlockAppearance,
  ContentBlock,
  CtaButton,
  HeroSlideData,
} from "@/lib/storefront-types";

type HeroSliderSectionProps = {
  block: Extract<ContentBlock, { kind: "heroSlider" }>;
  headingLevel?: 1 | 2;
};

const SWIPE_THRESHOLD = 48;

const heroHeightClass = "min-h-[30rem] sm:min-h-[34rem] lg:min-h-[72svh]";
const heroImageHeightClass = "min-h-[22rem] sm:min-h-[25rem] lg:min-h-[50svh]";

function wrapIndex(index: number, length: number) {
  return (index + length) % length;
}

function HeroSlideButtons({
  tone,
  buttons,
  className = "mt-8 justify-center",
}: {
  tone: ReturnType<typeof resolveHeroSliderTone>;
  buttons: CtaButton[];
  className?: string;
}) {
  const enabled = buttons.filter((button) => button.enabled);

  if (enabled.length === 0) {
    return null;
  }

  return (
    <div className={`flex flex-wrap gap-3 ${className}`}>
      {enabled.map((button) => (
        <Link
          key={`${button.label}-${button.href}`}
          href={button.href}
          target={button.target === "blank" ? "_blank" : undefined}
          rel={button.target === "blank" ? "noopener noreferrer" : undefined}
          className={getHeroSliderCtaClassName(tone, button.style)}
        >
          {button.label}
        </Link>
      ))}
    </div>
  );
}

function ImageSlide({
  appearance,
  headingLevel,
  slide,
  isActive,
}: {
  appearance: BlockAppearance;
  headingLevel: 1 | 2;
  slide: Extract<HeroSlideData, { type: "image" }>;
  isActive: boolean;
}) {
  const overlayOpacity = slide.overlayOpacity ?? 0;
  const tone = resolveHeroSliderTone(appearance);
  const isLight = tone === "light";
  const isFrozen = tone === "frozen";
  const articleClass = isLight
    ? "bg-[linear-gradient(180deg,#edf5ea_0%,#dfeadd_100%)]"
    : isFrozen
      ? "storefront-surface-frozen"
      : "storefront-surface-dark-alt";
  const overlayClass = isLight
    ? "bg-[radial-gradient(circle_at_50%_16%,rgba(255,255,255,0.22),transparent_34%),linear-gradient(180deg,rgba(255,255,255,0.12)_0%,rgba(0,0,0,0.08)_100%)]"
    : isFrozen
      ? "storefront-accent-radial"
      : "bg-[radial-gradient(circle_at_50%_16%,rgba(255,255,255,0.06),transparent_34%),linear-gradient(180deg,rgba(255,255,255,0.03)_0%,rgba(0,0,0,0.14)_100%)]";
  const eyebrowClass = isLight ? "text-[#234127]" : "text-white";
  const headlineClass = isLight ? "storefront-text-heading" : "text-white";
  const descriptionClass = isLight ? "text-[#325636]" : "text-white/82";
  const HeadlineTag = headingLevel === 1 ? "h1" : "h2";
  const isLongHeadline = slide.headline.length > 34;
  const headlineScaleClass = isLongHeadline
    ? "max-w-[20ch] text-[clamp(2rem,4.2vw,3rem)] leading-[1.4] tracking-[0.01em]"
    : "max-w-[16ch] text-[clamp(2.2rem,4.8vw,3.375rem)] leading-[1.4] tracking-[0.008em]";

  return (
    <article className={`relative overflow-hidden ${articleClass} ${heroImageHeightClass}`}>
      <div className={`absolute inset-0 ${overlayClass}`} />

      {slide.artwork?.url ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-[-8%] flex justify-center">
          <div className="relative h-[14rem] w-[14rem] opacity-[0.13] sm:h-[17rem] sm:w-[17rem] lg:h-[20rem] lg:w-[20rem]">
            <Image
              src={slide.artwork.url}
              alt=""
              fill
              priority={isActive}
              sizes="(min-width: 1024px) 20rem, 14rem"
              className="object-contain blur-[0.6px]"
            />
          </div>
        </div>
      ) : null}

      {overlayOpacity > 0 ? (
        <div
          className="absolute inset-0"
          style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity / 100})` }}
        />
      ) : null}

      <div
        className={`relative mx-auto flex max-w-[92rem] items-center justify-center px-5 py-8 text-center sm:px-8 sm:py-9 lg:px-10 lg:py-10 xl:px-14 ${heroImageHeightClass}`}
      >
        <div className="flex max-w-[66rem] flex-col items-center">
          {slide.eyebrow ? (
            <p
              className={`${heroDisplayFont.className} text-[clamp(1rem,1.8vw,1.2rem)] font-medium tracking-[0.16em] ${eyebrowClass}`}
            >
              {slide.eyebrow}
            </p>
          ) : null}

          <HeadlineTag
            className={`${heroDisplayFont.className} mt-3 ${headlineScaleClass} ${headlineClass}`}
          >
            {slide.headline}
          </HeadlineTag>

          {slide.description ? (
            <p
              className={`${heroBodyFont.className} mt-5 max-w-[38rem] text-[clamp(1rem,1.55vw,1.15rem)] font-medium leading-[1.55] ${descriptionClass}`}
            >
              {slide.description}
            </p>
          ) : null}

          {slide.buttons.length > 0 ? (
            <HeroSlideButtons tone={tone} buttons={slide.buttons} />
          ) : null}
        </div>
      </div>
    </article>
  );
}

function VideoSlide({
  appearance,
  headingLevel,
  slide,
  isActive,
}: {
  appearance: BlockAppearance;
  headingLevel: 1 | 2;
  slide: Extract<HeroSlideData, { type: "video" }>;
  isActive: boolean;
}) {
  const overlayOpacity = slide.overlayOpacity ?? 38;
  const hasCopy = Boolean(slide.eyebrow || slide.headline || slide.description || slide.buttons.length > 0);
  const tone = resolveHeroSliderTone(appearance);
  const isFrozen = tone === "frozen";
  const HeadlineTag = headingLevel === 1 ? "h1" : "h2";
  const isLongHeadline = (slide.headline?.length ?? 0) > 34;
  const headlineScaleClass = isLongHeadline
    ? "max-w-[18ch] text-[clamp(1.95rem,4.4vw,2.9rem)] leading-[1.4] tracking-[0.01em]"
    : "max-w-[15ch] text-[clamp(2.1rem,5vw,3.375rem)] leading-[1.4] tracking-[0.008em]";
  const articleClass = isFrozen
    ? "storefront-surface-frozen"
    : "bg-[radial-gradient(circle_at_top_left,rgba(0,150,57,0.18),transparent_24%),linear-gradient(135deg,#011806_0%,#05200c_46%,#000d03_100%)]";

  return (
    <article
      className={`relative overflow-hidden ${articleClass} ${heroHeightClass}`}
    >
      <div className="absolute inset-0 overflow-hidden">
        {isActive ? (
          <VideoPlayer
            url={slide.video.url}
            title={slide.video.title}
            immersive
          />
        ) : (
          <div className="h-full w-full bg-[radial-gradient(circle_at_center,rgba(17,162,68,0.16),transparent_42%),linear-gradient(135deg,#021004_0%,#031507_100%)]" />
        )}
      </div>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `linear-gradient(180deg,rgba(0,0,0,${overlayOpacity * 0.4 / 100})_0%,rgba(0,0,0,${overlayOpacity / 100})_100%)`,
        }}
      />

      {hasCopy ? (
        <div
          className={`relative mx-auto flex max-w-[90rem] items-end justify-center px-5 py-8 text-center sm:px-8 sm:py-10 lg:px-12 lg:py-12 xl:px-16 ${heroHeightClass}`}
        >
          <div className="flex max-w-[48rem] flex-col items-center pb-14 sm:pb-16">
            {slide.eyebrow ? (
              <p
                className={`${heroDisplayFont.className} text-[clamp(0.98rem,1.7vw,1.18rem)] font-medium tracking-[0.16em] text-white/84`}
              >
                {slide.eyebrow}
              </p>
            ) : null}

            {slide.headline ? (
              <HeadlineTag
                className={`${heroDisplayFont.className} mt-4 ${headlineScaleClass} text-white`}
              >
                {slide.headline}
              </HeadlineTag>
            ) : null}

            {slide.description ? (
              <p
                className={`${heroBodyFont.className} mt-5 max-w-[34rem] text-[clamp(1rem,1.8vw,1.12rem)] font-medium leading-[1.55] text-white/82`}
              >
                {slide.description}
              </p>
            ) : null}

            {slide.buttons.length > 0 ? (
              <HeroSlideButtons
                tone={tone}
                buttons={slide.buttons}
                className="mt-7 justify-center"
              />
            ) : null}
          </div>
        </div>
      ) : null}
    </article>
  );
}

export function HeroSliderSection({
  block,
  headingLevel = 2,
}: HeroSliderSectionProps) {
  const { slides, autoplay, autoplayInterval, loop } = block;
  const tone = resolveHeroSliderTone(block.appearance);
  const isLight = tone === "light";
  const isFrozen = tone === "frozen";
  const [currentSlide, setCurrentSlide] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goToSlide = useCallback(
    (index: number) => {
      if (loop) {
        setCurrentSlide(wrapIndex(index, slides.length));
      } else {
        setCurrentSlide(Math.max(0, Math.min(index, slides.length - 1)));
      }
    },
    [loop, slides.length],
  );

  const goToPreviousSlide = useCallback(() => {
    goToSlide(currentSlide - 1);
  }, [currentSlide, goToSlide]);

  const goToNextSlide = useCallback(() => {
    goToSlide(currentSlide + 1);
  }, [currentSlide, goToSlide]);

  useEffect(() => {
    if (!autoplay || slides.length <= 1) return;

    autoplayRef.current = setInterval(() => {
      setCurrentSlide((prev) =>
        loop
          ? wrapIndex(prev + 1, slides.length)
          : Math.min(prev + 1, slides.length - 1),
      );
    }, autoplayInterval || 5000);

    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
  }, [autoplay, autoplayInterval, loop, slides.length]);

  function handleTouchStart(event: TouchEvent<HTMLDivElement>) {
    const touch = event.changedTouches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
  }

  function handleTouchEnd(event: TouchEvent<HTMLDivElement>) {
    const touch = event.changedTouches[0];

    if (touchStartX.current === null || touchStartY.current === null) return;

    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;

    touchStartX.current = null;
    touchStartY.current = null;

    if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) < Math.abs(deltaY)) return;

    if (deltaX > 0) {
      goToPreviousSlide();
    } else {
      goToNextSlide();
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      goToPreviousSlide();
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      goToNextSlide();
    }
  }

  if (slides.length === 0) return null;

  return (
    <section
      aria-label="Hero slider"
      aria-roledescription="carousel"
      className={`relative isolate overflow-hidden ${
        isLight
          ? "bg-[linear-gradient(180deg,#f7fbf4_0%,#eef5ea_100%)] storefront-text-heading"
          : isFrozen
            ? "storefront-surface-frozen text-white"
            : "storefront-surface-dark text-white"
      }`}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div
        className="overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="flex transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {slides.map((slide, index) => {
            const isActive = index === currentSlide;
            // Every slide is in the DOM simultaneously, so an unguarded
            // headingLevel of 1 would render one H1 per slide. Only the first
            // slide may carry the page H1 — and only when it actually has
            // headline text, so a blank slide-0 headline never emits an empty
            // H1 (the homepage's sr-only fallback H1, which mirrors this
            // trim() guard, takes over instead); the rest are H2s.
            const slideHeadingLevel: 1 | 2 =
              headingLevel === 1 && index === 0 && slide.headline?.trim()
                ? 1
                : 2;

            return (
              <div
                key={slide.id}
                role="group"
                aria-roledescription="slide"
                aria-label={`${index + 1} of ${slides.length}`}
                aria-hidden={!isActive}
                className="min-w-full"
              >
                {slide.type === "image" ? (
                  <ImageSlide
                    appearance={block.appearance}
                    headingLevel={slideHeadingLevel}
                    slide={slide}
                    isActive={isActive}
                  />
                ) : (
                  <VideoSlide
                    appearance={block.appearance}
                    headingLevel={slideHeadingLevel}
                    slide={slide}
                    isActive={isActive}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {slides.length > 1 ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 sm:bottom-5">
          <div
            className={`mx-auto flex w-fit items-center gap-2 rounded-full border px-2 py-2 shadow-[0_18px_44px_-24px_rgba(0,0,0,0.5)] backdrop-blur-lg ${
              isLight
                ? "storefront-border-primary-soft bg-white/88"
                : isFrozen
                  ? "storefront-border-primary-soft bg-[rgba(var(--storefront-primary-dark-rgb),0.72)]"
                  : "border-white/10 bg-[#021006]/72"
            }`}
          >
            <button
              type="button"
              onClick={goToPreviousSlide}
              className={getHeroSliderNavButtonClassName(tone)}
              aria-label="Previous slide"
            >
              Prev
            </button>

            <div className="flex items-center gap-2 px-1">
              {slides.map((slide, index) => (
                <button
                  key={slide.id}
                  type="button"
                  onClick={() => goToSlide(index)}
                  className={`pointer-events-auto h-2.5 rounded-full transition-all duration-200 ${
                    index === currentSlide
                      ? "w-8 storefront-bg-primary"
                      : isLight
                        ? "w-2.5 bg-[rgba(var(--storefront-primary-rgb),0.28)] hover:bg-[rgba(var(--storefront-primary-rgb),0.5)]"
                        : "w-2.5 bg-white/35 hover:bg-white/55"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                  aria-pressed={index === currentSlide}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={goToNextSlide}
              className={getHeroSliderNavButtonClassName(tone)}
              aria-label="Next slide"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
